import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { closePanel } from '@/features/aiChat/aiChatSlice'
import { armSpotlight, clearSpotlight, selectPendingSpotlight } from '@/features/assist/assistSlice'
import { ASSIST_COPY, anchorMissMessage } from '@/features/assist/assistCopy'
import {
  ANCHOR_WAIT_MS,
  EDIT_TOGGLE_ANCHOR,
  REQUIRES_EDIT_MODE,
  RING_DISMISS_MS,
  findAnchor,
  isEditModeOn,
  logAssistMiss,
  routeOf,
  watchForAnchor,
} from '@/features/assist/spotlight'
import prefersReducedMotion from '@/components/ai-chat/prefersReducedMotion'
import SpotlightRing from './SpotlightRing'
import { isSheetWidth } from '@/components/ai-chat/sheetBreakpoint'

const MOBILE_MENU_ANCHORS = new Set(['nav.settings', 'nav.finish_rock_setup', 'nav.ask_graphit', 'nav.integrations', 'nav.getting_started'])
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * The target itself when it takes focus - including a roving-tabindex item
 * such as a Radix menu item (`tabindex="-1"`) - else its first focusable
 * descendant.
 */
function focusTarget(element) {
  if (element.matches?.(FOCUSABLE) || element.hasAttribute?.('tabindex')) return element
  return element.querySelector?.(FOCUSABLE) || null
}

/**
 * What has to be opened before the anchor exists: the Edit toggle for an
 * edit-mode anchor (unless edit mode is already on), else the anchor's
 * `parent` (a ⋮ menu trigger, the account menu…).
 */
function prerequisiteOf(pending) {
  if (pending.requires === REQUIRES_EDIT_MODE) {
    return isEditModeOn() ? null : { anchor: EDIT_TOGGLE_ANCHOR, tip: ASSIST_COPY.editFirstTip }
  }
  if (pending.parent) return { anchor: pending.parent, tip: ASSIST_COPY.parentFirstTip }
  if (MOBILE_MENU_ANCHORS.has(pending.anchor)) return { anchor: 'nav.menu', tip: 'Open More to find this destination.' }
  return null
}

/**
 * The element to ring for a pending spotlight: the anchor itself, or - while
 * it isn't on screen yet - the control that reveals it.
 */
function resolveTarget(pending) {
  const element = findAnchor(pending.anchor)
  if (element) return { element, tip: pending.tip, stage: 'target' }
  const prerequisite = prerequisiteOf(pending)
  const opener = prerequisite && findAnchor(prerequisite.anchor)
  return opener ? { element: opener, tip: prerequisite.tip, stage: 'prereq' } : null
}

/** Toast "{label} is under {route_hint}", log `assist_anchor_miss`, drop the pending spotlight. */
function useReportMiss() {
  const dispatch = useDispatch()
  return useCallback((armed, pathname, reason) => {
    dispatch(clearSpotlight())
    toast(anchorMissMessage(armed.anchor), { id: `assist-miss-${armed.anchor}` })
    logAssistMiss({ anchor: armed.anchor, path: armed.path || pathname, reason, ...(reason === 'redirected' ? { landed: pathname } : {}) })
  }, [dispatch])
}

/**
 * Follows the armed spotlight to its page and waits there for the anchor
 * (or the control that reveals it). Landing on a different path than the
 * card's `url_path` (any history entry after the click's - a redirect) is a
 * miss right away; never leaving the entry the card was clicked on is a
 * miss once the arm expires.
 */
function usePendingSpotlight(show) {
  const location = useLocation()
  const pending = useSelector(selectPendingSpotlight)
  const reportMiss = useReportMiss()

  useEffect(() => {
    if (!pending) return undefined
    if (pending.path && routeOf(location.pathname) !== routeOf(pending.path)) {
      if (location.key !== pending.fromKey) {
        reportMiss(pending, location.pathname, 'redirected')
        return undefined
      }
      const id = setTimeout(() => reportMiss(pending, location.pathname, 'not_reached'), Math.max(0, pending.expiresAt - Date.now()))
      return () => clearTimeout(id)
    }
    const pathname = location.pathname
    return watchForAnchor({
      find: () => resolveTarget(pending),
      timeoutMs: ANCHOR_WAIT_MS,
      onFound: (hit) => show(hit, pending, pathname),
      onTimeout: () => reportMiss(pending, pathname, 'not_found'),
    })
  }, [pending, location.pathname, location.key, reportMiss, show])
}

/**
 * While a prerequisite is ringed, keep watching for the real anchor and
 * hand over the moment it appears, however it was opened: a click, Enter or
 * Space on a Radix menu trigger (which never fires `click`), or a modal
 * menu's retargeted pointer events.
 */
function useHandover(active, handover) {
  useEffect(() => {
    if (!active || active.stage !== 'prereq') return undefined
    return watchForAnchor({
      find: () => findAnchor(active.anchor),
      timeoutMs: RING_DISMISS_MS,
      onFound: (element) => handover(element),
      onTimeout: () => {},
    })
  }, [active, handover])
}

/**
 * Moves focus to the ringed control and points its `aria-describedby` at the
 * tip, so a screen reader announces the tip with the control. Runs after the
 * tip has rendered; the control's own description is restored afterwards.
 */
function useFocusAndDescribe(active, tipId) {
  useLayoutEffect(() => {
    if (!active) return undefined
    const target = focusTarget(active.element)
    if (!target) return undefined
    const previous = target.getAttribute('aria-describedby')
    if (active.tip) target.setAttribute('aria-describedby', previous ? `${previous} ${tipId}` : tipId)
    target.focus({ preventScroll: true })
    return () => {
      if (previous == null) target.removeAttribute('aria-describedby')
      else target.setAttribute('aria-describedby', previous)
    }
  }, [active, tipId])
}

/**
 * Click outside, Esc, or the timeout ends the ring. Pressing the ringed
 * prerequisite doesn't: it is what opens the anchor. If it navigates
 * instead (a different page holds the anchor), the spotlight is re-armed
 * there.
 */
function useDismissal(active, dismiss) {
  const dispatch = useDispatch()
  const location = useLocation()
  const engaged = useRef(false)

  useEffect(() => {
    engaged.current = false
    if (!active) return undefined
    const inside = (event) => active.element.contains(event.target)
    const onPointerDown = (event) => {
      if (active.stage === 'prereq' && inside(event)) engaged.current = true
      else dismiss()
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') dismiss()
      else if (active.stage === 'prereq' && inside(event)) engaged.current = true
    }
    const onClick = (event) => { if (active.stage === 'prereq' && inside(event)) engaged.current = true }
    const timer = setTimeout(dismiss, RING_DISMISS_MS)
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('click', onClick, true)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('click', onClick, true)
    }
  }, [active, dismiss])

  useEffect(() => {
    if (!active || location.pathname === active.pathname) return
    if (active.stage === 'prereq' && engaged.current) {
      dispatch(armSpotlight({ anchor: active.anchor, tip: active.armedTip, path: null }))
    }
    dismiss()
  }, [active, location.pathname, dismiss, dispatch])
}

/**
 * Rings the control a `navigate` card's `spotlight` names, after the viewer
 * clicks the card (the only way a spotlight is armed). Mounted once in
 * RootLayout, so never under `/embed/*`.
 *
 * Once the route reaches the card's page it waits up to 4 s (a
 * MutationObserver, so a control that mounts late after data loads is still
 * found) for `[data-assist="<anchor>"]`, scrolls it to the middle of the
 * view, moves focus to it and draws the ring with the tip. The ring goes on
 * a click elsewhere, Esc, a route change, or after 8 s. When the anchor
 * never shows, or the page redirects elsewhere, a toast says where it lives
 * and an `assist_anchor_miss` event is logged.
 *
 * An anchor that only exists after another step (`requires: 'edit_mode'`
 * while edit mode is off, or a `parent` menu) first rings that control with
 * "Click Edit first" / "Open this first", then moves to the anchor as soon
 * as it appears.
 */
export default function AssistSpotlight() {
  const dispatch = useDispatch()
  const location = useLocation()
  const store = useStore()
  const tipId = useId()
  const [active, setActive] = useState(null)

  const reveal = useCallback((element) => {
    const reducedMotion = prefersReducedMotion()
    // On phones and tablets the assistant sheet must not cover the control.
    if (store.getState().aiChat?.panelOpen && isSheetWidth()) dispatch(closePanel())
    element.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' })
    return reducedMotion
  }, [dispatch, store])

  const show = useCallback((hit, armed, pathname) => {
    const reducedMotion = reveal(hit.element)
    setActive({ ...hit, anchor: armed.anchor, armedTip: armed.tip, pathname, reducedMotion })
    dispatch(clearSpotlight())
  }, [dispatch, reveal])

  const handover = useCallback((element) => {
    reveal(element)
    setActive((current) => current && { ...current, element, tip: current.armedTip, stage: 'target', pathname: location.pathname })
  }, [reveal, location.pathname])

  const dismiss = useCallback(() => setActive(null), [])

  usePendingSpotlight(show)
  useHandover(active, handover)
  useFocusAndDescribe(active, tipId)
  useDismissal(active, dismiss)

  if (!active || !active.element.isConnected) return null
  return <SpotlightRing element={active.element} tip={active.tip} tipId={tipId} reducedMotion={active.reducedMotion} />
}
